<!--
  Таблица ЭДО-документов.
  Показывает список документов, статусы, ссылки на карточку, Saby, XML/PDF и обновление.
-->
<template>
  <div class="edo-documents-table">
    <TableComponent
      v-if="tableColumns.length > 0 && documents.length > 0"
      :data="documents"
      :columns="tableColumns"
      :ready="ready"
      :loading="loading"
      @row-click="handleRowClick"
      @row-middle-click="openDocumentInNewTab"
    >
      <template #cell-document_type="{ item }">
        {{ documentTypeLabel(item) }}
      </template>

      <template #cell-document_type_name="{ item }">
        {{ documentTypeLabel(item) }}
      </template>

      <template #cell-status="{ item }">
        <span
          tabindex="0"
          class="doc-status"
          :class="statusClass(item.status)"
          :style="statusStyle(item.status)"
          :aria-label="statusTooltip(item.status)"
          @mouseenter="showStatusTooltip($event, item.status)"
          @mouseleave="hideStatusTooltip"
          @focus="showStatusTooltip($event, item.status)"
          @blur="hideStatusTooltip"
        >
          {{ item.status || "—" }}
        </span>
      </template>

      <template #cell-internal_status="{ item }">
        <BaseStatusSelect
          compact
          :status="item.internal_status"
          :options="internalStatusOptions"
          @change="handleInternalStatusChange(item, $event)"
        />
      </template>

      <template #cell-document_flow="{ item }">
        <EdoDocumentFlowCell :flow="item.document_flow" />
      </template>

      <template #cell-order="{ item }">
        <OrderEntityLink v-if="item.order" :order="item.order" />
        <span v-else>—</span>
      </template>

      <template #cell-provider="{ item }">
        {{ providerLabel(item.provider) }}
      </template>

      <template #cell-integration_label="{ item }">
        <button
          type="button"
          class="integration-card"
          :title="integrationTooltip(item)"
          @click.stop="openIntegration(item)"
        >
          <span class="integration-card__provider">
            {{ integrationProviderName(item) }}
          </span>
          <span class="integration-card__login">
            {{ integrationLogin(item) }}
          </span>
        </button>
      </template>

      <template #cell-integration_link="{ item }">
        <button
          type="button"
          class="integration-card"
          :title="integrationTooltip(item)"
          @click.stop="openIntegration(item)"
        >
          <span class="integration-card__provider">
            {{ integrationProviderName(item) }}
          </span>
          <span class="integration-card__login">
            {{ integrationLogin(item) }}
          </span>
        </button>
      </template>

    </TableComponent>

    <PaginationComponent
      v-if="pagination.pages > 1 && !loading"
      :page="pagination.page"
      :pages="pagination.pages"
      :series="pagination.series"
      @update:page="$emit('page-change', $event)"
    />

    <div
      v-if="statusTooltipState.visible"
      class="status-tooltip"
      :style="statusTooltipStyle"
    >
      {{ statusTooltipState.text }}
    </div>
  </div>
</template>

<script>
import TableComponent from "@/components/TableComponent.vue";
import PaginationComponent from "@/components/PaginationComponent.vue";
import EdoDocumentFlowCell from "@/components/Edo/shared/EdoDocumentFlowCell.vue";
import OrderEntityLink from "@/components/Order/OrderEntityLink.vue";

import { useTableConfigStore } from "@/stores/tableConfigStore";
import { useEdoDocumentStore } from "@/stores/edoDocumentStore";
import { useEdoDocumentStatusStore } from "@/stores/edoDocumentStatusStore";
import { useReferenceItemsStore } from "@/stores/referenceItemsStore";

import {
  edoDocumentTypeLabel,
  edoProviderLabel,
} from "@/utils/edoDocumentLabels";
import { openRouteInNewTab } from "@/utils/routeUtils";

export default {
  name: "EdoDocumentsTable",

  components: {
    EdoDocumentFlowCell,
    OrderEntityLink,
    TableComponent,
    PaginationComponent,
  },

  props: {
    documents: {
      type: Array,
      default: () => [],
    },
    pagination: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["page-change"],

  data() {
    return {
      tableColumns: [],
      statusTooltipState: {
        visible: false,
        text: "",
        top: 0,
        left: 0,
      },
    };
  },

  computed: {
    tableConfigStore() {
      return useTableConfigStore();
    },

    edoDocumentStatusStore() {
      return useEdoDocumentStatusStore();
    },

    edoDocumentStore() {
      return useEdoDocumentStore();
    },

    referenceItemsStore() {
      return useReferenceItemsStore();
    },

    internalStatusOptions() {
      return this.referenceItemsStore.selectItems.map((item) => ({
        value: String(item.id),
        label: item.name,
        color: item.color || "#64748b",
      }));
    },

    ready() {
      return !this.loading && this.tableColumns.length > 0;
    },

    statusTooltipStyle() {
      return {
        top: `${this.statusTooltipState.top}px`,
        left: `${this.statusTooltipState.left}px`,
      };
    },
  },

  methods: {
    // Загружает пользовательскую конфигурацию колонок или fallback из JSON
    async loadTableConfig() {
      try {
        const columns =
          await this.tableConfigStore.initTableConfiguration(
            "Edo::Document",
            "edo-documents"
          );
        this.tableColumns = columns.filter(
          (col) => col.enabled !== false
        );
        this.tableColumns = this.normalizeUserEdoColumns(this.tableColumns);

        if (this.tableColumns.length === 0) {
          const { loadTableColumns } = await import(
            "@/utils/tableFormatter"
          );
          this.tableColumns = this.normalizeFallbackEdoColumns(
            await loadTableColumns("edo-documents")
          );
        }
      } catch (error) {
        console.error(
          "Не удалось загрузить конфигурацию таблицы ЭДО:",
          error
        );
        try {
          const { loadTableColumns } = await import(
            "@/utils/tableFormatter"
          );
          this.tableColumns = this.normalizeFallbackEdoColumns(
            await loadTableColumns("edo-documents")
          );
        } catch (fallbackError) {
          console.error("Ошибка загрузки таблицы ЭДО:", fallbackError);
          this.tableColumns = [];
        }
      }
    },

    // Для пользовательских настроек сохраняем состав, порядок и ширины колонок из конфига.
    normalizeUserEdoColumns(columns) {
      return (Array.isArray(columns) ? columns : []).map((col) =>
        this.normalizeUserEdoColumn(col)
      );
    },

    // Для fallback-схемы можно применить защитные правки, потому что это не пользовательский конфиг.
    normalizeFallbackEdoColumns(columns) {
      const hiddenKeys = new Set([
        "actions",
        "external_status_note",
        "saby_status_note",
        "status_note",
        "note",
      ]);
      const normalizedColumns = (Array.isArray(columns) ? columns : [])
        .filter((col) => !hiddenKeys.has(col.key))
        .map((col) => this.normalizeEdoColumn(col));

      // Порядок из стандартного конфига сохраняем: дата идёт перед маршрутом,
      // а этапы остаются правее основных данных документа.
      return this.ensureDocumentFlowColumn(normalizedColumns);
    },

    // Сохраняет пользовательские подписи и ширины, но чинит технические параметры спец-колонок.
    normalizeUserEdoColumn(column) {
      if (column.key === "transport_order_flow") {
        return {
          ...column,
          key: "document_flow",
          sortable: false,
        };
      }

      if (column.key === "document_flow") {
        return { ...column, sortable: false };
      }

      if (["integration_label", "integration_link"].includes(column.key)) {
        return { ...column, sortable: false };
      }

      return column;
    },

    // Переименовывает и ужимает отдельные колонки без изменения backend-контракта.
    normalizeEdoColumn(column) {
      if (["document_flow", "transport_order_flow"].includes(column.key)) {
        return {
          ...column,
          key: "document_flow",
          label: "Этапы",
          minWidth: 260,
          sortable: false,
        };
      }

      if (["integration_label", "integration_link"].includes(column.key)) {
        return { ...column, minWidth: 150, sortable: false };
      }

      return column;
    },

    // Добавляет новую колонку этапов даже для старых пользовательских конфигураций таблицы.
    ensureDocumentFlowColumn(columns) {
      if (columns.some((col) => col.key === "document_flow")) return columns;

      const flowColumn = {
        key: "document_flow",
        label: "Этапы",
        type: "text",
        sortable: false,
        minWidth: 260,
      };
      const documentTypeIndex = columns.findIndex((col) => col.key === "document_type");
      const insertIndex = documentTypeIndex >= 0 ? documentTypeIndex + 1 : 3;
      const nextColumns = [...columns];
      nextColumns.splice(insertIndex, 0, flowColumn);

      return nextColumns;
    },

    // Возвращает человекочитаемый тип документа
    documentTypeLabel(item) {
      return edoDocumentTypeLabel(item);
    },

    // Возвращает человекочитаемый провайдер ЭДО
    providerLabel(provider) {
      return edoProviderLabel(provider);
    },

    // Показывает в таблице только логин интеграции, чтобы колонка не разъезжалась.
    integrationLogin(item) {
      return item.integration_card?.login || item.integration_login || "—";
    },

    // Короткое название провайдера оставляем как маленькую метку внутри карточки.
    integrationProviderName(item) {
      return item.integration_card?.provider_name || this.providerLabel(item.provider);
    },

    // В tooltip оставляем реквизиты, которые раньше занимали место прямо в таблице.
    integrationTooltip(item) {
      const card = item.integration_card || {};
      const details = [
        card.provider_name || this.providerLabel(item.provider),
        card.subtitle,
        card.enabled === false ? "Отключена" : "Включена",
      ].filter(Boolean);

      return details.join("\n");
    },

    async handleInternalStatusChange(document, statusId) {
      if (this.edoDocumentStore.isSubmitting) return;

      await this.edoDocumentStore.updateInternalDetails(document.id, {
        internal_status_id: statusId,
      });
    },

    // Клик по карточке ведёт в настройки интеграций, если backend дал ссылку.
    openIntegration(item) {
      const url = item.integration_card?.url || item.integration_url;
      if (!url) return;

      this.$router.push(url);
    },

    // Подбирает CSS-класс статуса по тексту из backend/Saby
    statusClass(status) {
      return this.edoDocumentStatusStore.statusClass(status);
    },

    // Берёт цвета статуса из единого store, чтобы таблица и карточка совпадали
    statusStyle(status) {
      return this.edoDocumentStatusStore.statusStyle(status);
    },

    // Показывает пользователю смысл статуса и следующий шаг при наведении
    statusTooltip(status) {
      return this.edoDocumentStatusStore.statusTooltip(status);
    },

    // Показывает fixed-tooltip поверх таблицы, чтобы его не резали overflow ячейки
    showStatusTooltip(event, status) {
      const rect = event.currentTarget.getBoundingClientRect();
      this.statusTooltipState = {
        visible: true,
        text: this.statusTooltip(status),
        top: rect.top - 10,
        left: rect.left + rect.width / 2,
      };
    },

    // Скрывает tooltip статуса при уходе мыши или фокуса
    hideStatusTooltip() {
      this.statusTooltipState.visible = false;
    },

    // Открывает карточку документа по клику на строку
    handleRowClick(item) {
      this.$router.push({
        name: "EdoDocumentDetails",
        params: { id: item.id },
      });
    },

    // Открывает карточку документа в новой вкладке из контекстного действия
    openDocumentInNewTab(item) {
      openRouteInNewTab({
        name: "EdoDocumentDetails",
        params: { id: item.id },
      });
    },
  },

  async mounted() {
    await Promise.all([
      this.loadTableConfig(),
      this.referenceItemsStore.fetchSelectItems({
        targetModel: "Edo::Document",
        targetField: "internal_status_id",
      }),
    ]);
    window.addEventListener("scroll", this.hideStatusTooltip, true);
    window.addEventListener("resize", this.hideStatusTooltip);
  },

  beforeUnmount() {
    window.removeEventListener("scroll", this.hideStatusTooltip, true);
    window.removeEventListener("resize", this.hideStatusTooltip);
  },
};
</script>

<style scoped>
.edo-documents-table {
  width: 100%;
}

.doc-status {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 13px;
  white-space: nowrap;
  border: 1px solid transparent;
  background: #f3f4f6;
  color: #374151;
  cursor: help;
  outline: none;
}

.integration-card {
  display: inline-grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  max-width: 180px;
  min-height: 30px;
  padding: 4px 8px;
  column-gap: 6px;
  border: 1px solid #d8dee8;
  border-radius: 6px;
  background: #f8fafc;
  color: #1f2937;
  cursor: pointer;
  font: inherit;
  text-align: left;
}

.integration-card:hover {
  border-color: #93c5fd;
  background: #eff6ff;
}

.integration-card__provider {
  padding: 1px 5px;
  border-radius: 4px;
  background: #e0f2fe;
  color: #0369a1;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.3;
  text-transform: uppercase;
}

.integration-card__login {
  overflow: hidden;
  min-width: 0;
  color: #111827;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status-tooltip {
  position: fixed;
  z-index: 9999;
  width: max-content;
  max-width: 280px;
  padding: 8px 10px;
  border-radius: 6px;
  background: #111827;
  color: #fff;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.35;
  text-align: left;
  white-space: normal;
  pointer-events: none;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.18);
  transform: translate(-50%, -100%);
}

.status-tooltip::after {
  content: "";
  position: absolute;
  left: 50%;
  top: 100%;
  width: 0;
  height: 0;
  border: 5px solid transparent;
  border-top-color: #111827;
  transform: translateX(-50%);
}
</style>
